import React, { FC, useMemo } from 'react';
import { Wrapper as GoogleMapWrapper } from '@googlemaps/react-wrapper';
import { Map } from './Map/Map';
import { GoogleMapMarker } from '../../interfaces/googleMap/GoogleMapMarker';
import MapLocationInfo from '../Templates/DefaultTemplate/Components/MapLocationInfo/MapLocationInfo';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { ILocation } from '../../interfaces/professional/Professional';

interface Props {
  serviceId: string;
  otherLocations: Array<ILocation>;
  primaryLocation: ILocation;
  mobilityRadius: number | null;
  primaryLocationMarkerIcon: string;
  otherLocationsMarkerIcon: string;
  linkComponent?: JSX.Element;
  legendComponent?: JSX.Element;
  radiusColor?: string;
  phoneNumber: string;
}

const MapWrapper: FC<Props> = ({
  otherLocationsMarkerIcon,
  primaryLocationMarkerIcon,
  primaryLocation,
  otherLocations,
  serviceId,
  mobilityRadius,
  legendComponent,
  linkComponent,
  radiusColor,
  phoneNumber,
}) => {
  const { t } = useTranslation();
  const markers: Array<GoogleMapMarker> = useMemo(() => {
    const primary = {
      isPrimary: true,
      circleRadius: mobilityRadius,
      position: { lat: primaryLocation.latitude, lng: primaryLocation.longitude },
      infoWindowContent: (
        <MapLocationInfo
          name={primaryLocation.name}
          phone={phoneNumber}
          address={primaryLocation.placeDescription}
          buttonLink={t('services.locationsSection.map.getDirectionButtonLink')}
          buttonTitle={t('services.locationsSection.map.getDirectionButtonTitle')}
          buttonText={t('services.locationsSection.map.getDirectionButtonText')}
          latitude={primaryLocation.latitude}
          longitude={primaryLocation.longitude}
          linkToGMapsText={t('services.locationsSection.map.viewOnGoogleMap')}
        />
      ),
    };
    const result = otherLocations.map((item, index) => {
      return {
        markerId: index,
        circleRadius: mobilityRadius,
        position: { lat: item.latitude, lng: item.longitude },
        infoWindowContent: (
          <MapLocationInfo
            name={item.name}
            phone={phoneNumber}
            address={item.placeDescription}
            buttonLink={t('services.locationsSection.map.getDirectionButtonLink')}
            buttonTitle={t('services.locationsSection.map.getDirectionButtonTitle')}
            buttonText={t('services.locationsSection.map.getDirectionButtonText')}
            latitude={item.latitude}
            longitude={item.longitude}
            linkToGMapsText={t('services.locationsSection.map.viewOnGoogleMap')}
          />
        ),
        isPrimary: false,
      } as GoogleMapMarker;
    });
    return [...result, primary as GoogleMapMarker];
  }, [
    mobilityRadius,
    otherLocations,
    phoneNumber,
    primaryLocation.latitude,
    primaryLocation.longitude,
    primaryLocation.name,
    primaryLocation.placeDescription,
    t,
  ]);

  return (
    <GoogleMapWrapper apiKey={process.env.GATSBY_GOOGLE_MAP_API_KEY as string} libraries={['places']}>
      <Map
        key={serviceId}
        primaryLocation={{ lng: primaryLocation.longitude, lat: primaryLocation.latitude }}
        markers={markers}
        primaryLocationMarkerIcon={primaryLocationMarkerIcon}
        otherLocationsMarkerIcon={otherLocationsMarkerIcon}
        legendComponent={legendComponent}
        linkComponent={linkComponent}
        radiusColor={radiusColor}
      />
    </GoogleMapWrapper>
  );
};

export default MapWrapper;
